import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import AppBar from '@material-ui/core/AppBar';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import { loadApplicantAutomations } from '../applicant-automations/actions/load-applicant-automations';
import {
  applicantAutomationsForOrgIdSelector,
  applicantAutomationsStateForOrgIdSelector,
} from '../applicant-automations/applicant-automation-selectors';
import { loadOrgJobs } from '../job/actions/load-org-jobs';
import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import { jobsForOrgIdSelector, jobsStateForOrgIdSelector } from '../reducers/job-selectors';
import { orgSelector } from '../reducers/org-selectors';

import { FEATURE_USAGE_TASKS } from '../feature-usage-task/feature-usage-task-utils';
import { UiBreadcrumbHeader } from '../ui/ui-breadcrumb-header';
import { jobNeedReviewRoute } from '../web-routes';

import { OrgProcessAutomations } from './org-process-automations';

import { AutomationTimelineSection } from './components/widgets/automation-timeline-section';
import {
  canEditApplicantAutomationOrgDefaults,
  canViewApplicantAutomations,
  canViewEmployeeAutomations,
} from './utils/automation-permission-utils';
import { JobDropdownBase, JobSelectorOption } from '../job/widgets/job-dropdown-base';
import { formatLocation } from '../util/formatters';
import { ACTIVE_STATUSES } from '../util/job-status';
import { sortJobsByTitleActiveLocation } from '../util/sort-functions';
import { useThunkDispatch } from '../util/use-thunk-dispatch';
import Message from '../widgets/legacy-message';
import { LoadingMessage } from '../widgets/loading-message';

const TABS = {
  PROCESS_AUTOMATIONS: 'Process Automations',
  SPECIAL_OCCASION_AUTOMATIONS: 'Special Occasion Automations',
};
type AutomationTab = ValueOf<typeof TABS>;
type AutomationTabKey = keyof typeof TABS;

export const OrgAutomations = ({
  orgId,
  tab,
  queryParams,
}: {
  orgId: number;
  tab?: string | string[] | null;
  queryParams?: {
    jobId?: number | null;
    focusApplicantAutomationId?: string | null;
  };
}) => {
  const history = useHistory();
  const user = useSelector((state: any) => userSelector(state));
  const jobs: Job[] = useSelector((state: any) => jobsForOrgIdSelector(state, orgId)) || [];
  const jobsState = useSelector((state: any) => jobsStateForOrgIdSelector(state, orgId)) || {};
  const applicantAutomations =
    useSelector((state: any) => applicantAutomationsForOrgIdSelector(state, orgId)) || [];
  const applicantAutomationsState = useSelector(
    (state: any) => applicantAutomationsStateForOrgIdSelector(state, orgId) || {},
  );

  const focusAutomationId = queryParams?.focusApplicantAutomationId
    ? Number.parseInt(queryParams.focusApplicantAutomationId, 10)
    : null;
  const focusApplicantAutomation = applicantAutomations.find(
    (a) => a.applicantTriggeredCommunicationId === focusAutomationId,
  );

  const hasApplicantAutomationViewPermission = canViewApplicantAutomations(user);
  const hasEmployeeAutomationViewPermission = canViewEmployeeAutomations(user);
  const hasApplicantAutomationOrgDefaultEditPermission =
    canEditApplicantAutomationOrgDefaults(user);

  const sortedJobOptions: JobSelectorOption[] = sortJobsByTitleActiveLocation(jobs).map((job) => ({
    label: job.title,
    value: job.job_id.toString(),
    optionGroup: formatLocation(job) || '',
    isActive: ACTIVE_STATUSES.includes(job.status),
  }));

  const sortedOptions: JobSelectorOption[] = hasApplicantAutomationOrgDefaultEditPermission
    ? [
        { label: 'Organization Defaults', value: 'orgDefault', optionGroup: 'orgDefault' },
        ...sortedJobOptions,
      ]
    : [...sortedJobOptions];

  const isValidTab = tab && !Array.isArray(tab) && Object.keys(TABS).includes(tab);
  const [selectedTab, setSelectedTab] = useState<AutomationTab>(
    isValidTab && hasEmployeeAutomationViewPermission && tab === 'SPECIAL_OCCASION_AUTOMATIONS'
      ? TABS[tab as AutomationTabKey]
      : TABS.PROCESS_AUTOMATIONS,
  );

  const [selectedJobOptionValue, setSelectedJobOptionValue] = useState<string>(
    queryParams?.jobId?.toString() || '',
  );
  const selectedJobOption =
    sortedOptions.find((o) => o.value === selectedJobOptionValue) || sortedOptions[0];
  const selectedJob = jobs.find((job) => job.job_id.toString() === selectedJobOption.value);

  const dispatch = useThunkDispatch();

  useEffect(() => {
    dispatch(loadOrg(orgId));
    if (hasApplicantAutomationViewPermission) {
      dispatch(loadApplicantAutomations({ orgId }));
      dispatch(loadOrgJobs(orgId));
    }
  });

  const org = useSelector((state: any) => orgSelector(state, orgId));

  const handleTabChange = (newTab: AutomationTab) => () => {
    if (newTab === TABS.SPECIAL_OCCASION_AUTOMATIONS && !hasEmployeeAutomationViewPermission) {
      return;
    }

    setSelectedTab(newTab);
  };

  if (!org || jobsState.loading || applicantAutomationsState.loading) {
    return <LoadingMessage />;
  }
  const handleChangeSelectedJobOption = (selectedOption: JobSelectorOption) => {
    setSelectedJobOptionValue(selectedOption.value);
  };
  const noJobsForTimeline = jobs.length === 0;

  const handleViewJob = () => {
    history.push(jobNeedReviewRoute(selectedJob?.orgId, selectedJob?.job_id));
  };

  const disableApplicantAutomations =
    !hasApplicantAutomationViewPermission ||
    (!hasApplicantAutomationOrgDefaultEditPermission && noJobsForTimeline);

  return (
    <>
      <div
        className="u-position-top-sticky u-zIndex-99 u-background-lighterGray"
        style={{ marginBottom: 50 }}
      >
        <UiBreadcrumbHeader
          pageTitle="Automations"
          subsectionTitleWithSelector={
            selectedTab === TABS.PROCESS_AUTOMATIONS &&
            hasApplicantAutomationViewPermission &&
            selectedJobOption ? (
              <JobDropdownBase
                orgId={orgId}
                selectedOption={selectedJobOption}
                filterOptions={sortedJobOptions}
                allOptions={sortedOptions}
                handleChange={handleChangeSelectedJobOption}
              />
            ) : undefined
          }
          primaryActions={
            selectedJob ? [{ name: 'View Job', handler: handleViewJob, variant: 'tertiary' }] : []
          }
        />
        <AppBar position="static" color="default">
          <Tabs
            value={selectedTab === TABS.PROCESS_AUTOMATIONS ? 0 : 1}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab
              label={TABS.PROCESS_AUTOMATIONS}
              key={`tab-label-${TABS.PROCESS_AUTOMATIONS}`}
              onClick={handleTabChange(TABS.PROCESS_AUTOMATIONS)}
            />
            <Tab
              label={TABS.SPECIAL_OCCASION_AUTOMATIONS}
              key={`tab-label-${TABS.SPECIAL_OCCASION_AUTOMATIONS}`}
              onClick={handleTabChange(TABS.SPECIAL_OCCASION_AUTOMATIONS)}
              disabled={!hasEmployeeAutomationViewPermission}
            />
          </Tabs>
        </AppBar>
      </div>
      {hasApplicantAutomationViewPermission && disableApplicantAutomations && (
        <Message
          variant="error"
          canBeDismissed={false}
          className="u-margin-0Auto u-margin-y-xl u-width-80"
        >
          Your organization currently has no jobs to customize automations for. Once a job is
          created, you will be able to view and edit the automation timeline. To configure
          organization default messages before you begin hiring, please contact your account admin.
          You can also{' '}
          <a
            href={FEATURE_USAGE_TASKS.find((task) => task.name === 'ADD_JOB')?.details}
            target="_blank"
            rel="noopener noreferrer"
            className="typo-link"
          >
            get started by adding a job.
          </a>
          {hasEmployeeAutomationViewPermission && (
            <div className="u-margin-top-xs">
              You can still manage your employee tenure automations below
            </div>
          )}
        </Message>
      )}
      {selectedTab === TABS.PROCESS_AUTOMATIONS && (
        <OrgProcessAutomations
          org={org}
          jobs={jobs || []}
          selectedJob={selectedJob}
          focusApplicantAutomation={focusApplicantAutomation}
        />
      )}
      {selectedTab === TABS.SPECIAL_OCCASION_AUTOMATIONS && (
        <AutomationTimelineSection title="Birthdays" sectionKey="birthday" org={org} />
      )}
    </>
  );
};
